import React from "react";
import { resolveQuestionsForResult } from "../../utils/stats";
import { formatAdminDateTime } from "../../utils/date";
import { formatTimeTaken } from "../../utils/quiz";
import { compareTypedChars } from "../../utils/typingTest";

const OPTION_LABELS = ["A", "B", "C", "D"];

const getQuestionPreview = (text, maxLen = 72) => {
  if (!text) return "";
  const firstLine = text.split(/\r?\n/)[0].trim();
  if (firstLine.length <= maxLen) return firstLine;
  return `${firstLine.slice(0, maxLen)}…`;
};

const INFO_FIELDS = [
  { key: "aadharNumber", labelKey: "aadharNumber" },
  { key: "gender", labelKey: "gender" },
  { key: "dob", labelKey: "dob" },
  { key: "education", labelKey: "education" },
  { key: "medicalExp", labelKey: "medicalExp" },
  { key: "totalExp", labelKey: "totalExp" },
  { key: "prevCompany", labelKey: "prevCompany" },
  { key: "expectedSalary", labelKey: "expectedSalary" },
  { key: "availability", labelKey: "availability" },
  { key: "currentAddress", labelKey: "currentAddress" },
];

const hasTypingResults = (result) => {
  const typing = result?.typingTest;
  if (!typing || typeof typing !== "object") return false;
  return (
    typing.wpm != null
    || typing.accuracy != null
    || typing.totalChars > 0
    || Boolean(typing.passage)
    || Boolean(typing.typedText)
  );
};

const ParticipantDetail = ({ result, questions, t, onBack }) => {
  const shownQuestions = resolveQuestionsForResult(result, questions);
  const resultLang = result.lang || "en";
  const typing = hasTypingResults(result) ? result.typingTest : null;

  let correctCount = 0;
  let incorrectCount = 0;

  result.answers?.forEach((ans, i) => {
    const q = shownQuestions[i];
    if (!q || q.type === "text") return;
    if (ans === q[resultLang]?.answer) correctCount += 1;
    else incorrectCount += 1;
  });

  const scorePct = result.total > 0 ? Math.round((result.score / result.total) * 100) : 0;
  const submittedAt = formatAdminDateTime(result.timestamp);
  const initials = result.candidate.fullName?.charAt(0)?.toUpperCase() || "?";

  return (
    <div className="pd-page">
      <button type="button" className="pd-back" onClick={onBack}>
        <span className="pd-back-icon">←</span>
        {t.backToResults}
      </button>

      <div className="pd-hero">
        <div className="pd-hero-left">
          <div className="pd-avatar">{initials}</div>
          <div>
            <h2 className="pd-name">{result.candidate.fullName}</h2>
            <div className="pd-meta">
              <span className="pd-meta-item">
                <span className="pd-meta-icon">📞</span>
                {result.candidate.phoneNumber}
              </span>
              <span className="pd-meta-item">
                <span className="pd-meta-icon">📅</span>
                {submittedAt}
              </span>
            </div>
          </div>
        </div>
        <div className="pd-hero-score">
          <div className="pd-score-ring">
            <span className="pd-score-value">{scorePct}%</span>
          </div>
          <span className="pd-score-label">{result.score} / {result.total} {t.correct}</span>
        </div>
      </div>

      <div className="pd-performance-grid">
        <div className="pd-panel pd-panel-quiz">
          <div className="pd-panel-head">
            <span className="pd-panel-icon">📝</span>
            <h3>{t.quizPerformance}</h3>
          </div>
          <div className="pd-stat-grid">
            <div className="pd-stat pd-stat-success">
              <span className="pd-stat-value">{correctCount}</span>
              <span className="pd-stat-label">{t.correct}</span>
            </div>
            <div className="pd-stat pd-stat-danger">
              <span className="pd-stat-value">{incorrectCount}</span>
              <span className="pd-stat-label">{t.incorrect}</span>
            </div>
            <div className="pd-stat">
              <span className="pd-stat-value">
                {result.timeTakenSeconds ? formatTimeTaken(result.timeTakenSeconds) : "—"}
              </span>
              <span className="pd-stat-label">{t.timeTaken}</span>
            </div>
            <div className="pd-stat">
              <span className="pd-stat-value">{result.total}</span>
              <span className="pd-stat-label">{t.questionsLabel}</span>
            </div>
          </div>
        </div>

        <div className="pd-panel pd-panel-typing">
          <div className="pd-panel-head">
            <span className="pd-panel-icon">⌨️</span>
            <h3>{t.typingPerformance}</h3>
          </div>
          {typing ? (
            <div className="pd-stat-grid">
              <div className="pd-stat pd-stat-primary">
                <span className="pd-stat-value">{typing.wpm}</span>
                <span className="pd-stat-label">{t.typingWpm}</span>
              </div>
              <div className="pd-stat pd-stat-primary">
                <span className="pd-stat-value">{typing.accuracy}%</span>
                <span className="pd-stat-label">{t.typingAccuracy}</span>
              </div>
              <div className="pd-stat">
                <span className="pd-stat-value">{typing.totalChars}</span>
                <span className="pd-stat-label">{t.typingChars}</span>
              </div>
              <div className="pd-stat">
                <span className="pd-stat-value">{typing.durationSeconds ?? 30}s</span>
                <span className="pd-stat-label">{t.typingDuration}</span>
              </div>
            </div>
          ) : (
            <div className="pd-empty-typing">
              <span className="pd-empty-icon">⌨️</span>
              <p>{t.noTypingData}</p>
            </div>
          )}
        </div>
      </div>

      {typing && (
        <section className="pd-section">
          <div className="pd-section-head">
            <h3>{t.typingTestReview}</h3>
            <div className="pd-typing-badges">
              <span className="pd-badge pd-badge-primary">{t.typingWpm}: {typing.wpm}</span>
              <span className="pd-badge pd-badge-primary">{t.typingAccuracy}: {typing.accuracy}%</span>
              <span className="pd-badge">{t.typingChars}: {typing.totalChars}</span>
              <span className="pd-badge pd-badge-success">{t.correct}: {typing.correctChars}</span>
              <span className="pd-badge pd-badge-danger">{t.incorrect}: {typing.incorrectChars}</span>
            </div>
          </div>
          <div className="pd-typing-passage-card">
            <p className="pd-typing-passage-label">{t.typingPassage}</p>
            <div className="pd-typing-passage">
              {compareTypedChars(typing.passage || "", typing.typedText || "").map((item, index) => (
                <span key={index} className={`typing-char typing-char-${item.status}`}>
                  {item.char === " " ? "\u00a0" : item.char}
                </span>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="pd-section">
        <div className="pd-section-head">
          <h3>{t.candidateInfo}</h3>
        </div>
        <div className="pd-info-grid">
          {INFO_FIELDS.map(({ key, labelKey }) => {
            const value = result.candidate[key];
            if (!value) return null;
            return (
              <div key={key} className="pd-info-item">
                <span className="pd-info-label">{t[labelKey]}</span>
                <span className="pd-info-value">{value}</span>
              </div>
            );
          })}
        </div>
      </section>

      <section className="pd-section">
        <div className="pd-section-head">
          <h3>{t.examReview}</h3>
          <span className="pd-section-count">
            {result.answers?.length || 0} {t.questionsTotal}
          </span>
        </div>
        <div className="pd-review-list">
          {result.answers?.map((ans, i) => {
            const q = shownQuestions[i];
            if (!q) {
              return (
                <details key={i} className="pd-review-details pd-review-missing" open>
                  <summary className="pd-review-summary">
                    <span className="pd-review-num">Q{i + 1}</span>
                    <span className="pd-review-preview">{t.questionDataMissing}</span>
                    <span className="pd-review-badge pd-review-badge-muted">{t.questionDataMissing}</span>
                  </summary>
                  <div className="pd-review-body">
                    <p className="pd-review-answer">{t.userAnswer}: {ans || t.noAnswer}</p>
                  </div>
                </details>
              );
            }

            const content = q[resultLang] || q.en;
            const isText = q.type === "text";
            const isCorrect = !isText && ans === content.answer;

            return (
              <details
                key={i}
                className={`pd-review-details ${isText ? "" : isCorrect ? "pd-review-correct" : "pd-review-wrong"}`}
                open={!isCorrect}
              >
                <summary className="pd-review-summary">
                  <span className="pd-review-num">Q{i + 1}</span>
                  <span className="pd-review-preview">{getQuestionPreview(content.question)}</span>
                  {!isText && (
                    <span className={`pd-review-badge ${isCorrect ? "pd-review-badge-success" : "pd-review-badge-danger"}`}>
                      {isCorrect ? t.correct : t.incorrect}
                    </span>
                  )}
                  <span className="pd-review-chevron" aria-hidden="true">▾</span>
                </summary>

                <div className="pd-review-body">
                  <p className="pd-review-question">{content.question}</p>

                  {!isText && content.options && (
                    <div className="pd-review-options">
                      {content.options.map((opt, oIdx) => {
                        const isAnswer = opt === content.answer;
                        const isUser = opt === ans;
                        return (
                          <div
                            key={oIdx}
                            className={`pd-review-opt ${isAnswer ? "pd-review-opt-correct" : ""} ${isUser && !isAnswer ? "pd-review-opt-wrong" : ""}`}
                          >
                            <span className="pd-review-opt-letter">{OPTION_LABELS[oIdx]}</span>
                            <span className="pd-review-opt-text">{opt}</span>
                            {isUser && <span className="pd-review-tag">{t.userAnswer}</span>}
                            {isAnswer && <span className="pd-review-tag pd-review-tag-correct">{t.correctAnswer}</span>}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {isText && (
                    <p className="pd-review-answer">
                      <strong>{t.userAnswer}:</strong> {ans || t.noAnswer}
                    </p>
                  )}
                </div>
              </details>
            );
          })}
        </div>
      </section>
    </div>
  );
};

export default ParticipantDetail;
